import { Component, inject, ViewChild, signal } from '@angular/core';
import { TabComponent } from '../../../components/tab/tab.component';
import { SelectComponent } from '../../../components/select/select.component';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { FilterOption } from '../../../components/table/table.interface';
import { ReactiveFormsModule } from '@angular/forms';
import { ButtonDirective } from '../../../components/button/button.directive';
import { GrantLeavesService } from '../../../services/leave-management/grant-leaves.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { GrantLeavesData, UsersInterface } from './grant-leaves.interface';

@Component({
  selector: 'app-grant-leaves',
  imports: [SelectComponent, ReactiveFormsModule, ButtonDirective],
  providers: [{ provide: TabComponent, useExisting: GrantLeavesComponent }],
  templateUrl: './grant-leaves.component.html',
  styleUrl: './grant-leaves.component.scss',
})
export class GrantLeavesComponent extends TabComponent {
  @ViewChild('usersSelect') usersSelect!: SelectComponent;
  @ViewChild('typeSelect') typeSelect!: SelectComponent;

  grantLeavesService = inject(GrantLeavesService);
  snackbarService = inject(SnackbarService);
  users: string[] = [];
  usersIds: string[] = [];
  leaveTypes: string[] = [];
  override name: string = this.dictionaryService.get('GrantLeaves');
  
  submitting = signal(false);
  public async main(): Promise<void> {
    this.grantLeavesService.getUsersList().subscribe({
      next: (users) => {
        this.users = users.map((user) => user.name);
        this.usersIds = users.map((user) => user.id);
      },
    });

    this.grantLeavesService.getLeavesTypes().subscribe({
      next: (types) => {
        this.leaveTypes = types;
      },
    });
  }

  protected grantLeavesForm = new FormGroup({
    leaveType: new FormControl<string>('', Validators.required),
    subCategory: new FormControl<string>('', Validators.required),
    from: new FormControl<Date | null>(null, Validators.required),
    to: new FormControl<Date | null>(null, Validators.required),
    isNonFieldActivity: new FormControl<boolean>(false, { nonNullable: true }),
    usersIds: new FormControl<string[]>([], [Validators.required]),
  });

  changeLeaveType = (type: FilterOption) => {
    this.grantLeavesForm.patchValue({
      leaveType: type.value,
    });
    this.grantLeavesForm.controls.leaveType.markAsTouched();
    this.grantLeavesForm.controls.leaveType.updateValueAndValidity();
  };

  checkUser = (usersIds: string[]) => {
    this.grantLeavesForm.patchValue({
      usersIds: usersIds,
    });

    if (!this.submitting()) {
      this.grantLeavesForm.controls.usersIds.markAsTouched();
      this.grantLeavesForm.controls.usersIds.updateValueAndValidity();
    }
  };

  get f() {
    return this.grantLeavesForm.controls;
  }

  grantLeaves() {
    if (this.grantLeavesForm.invalid) {
      this.grantLeavesForm.markAllAsTouched();
      return;
    }

    this.submitting.set(true);
    const formValue = this.grantLeavesForm.getRawValue();

    this.grantLeavesService.grantLeaves(formValue).subscribe({
      next: (res) => {
        if (res?.success) {
          this.snackbarService.showSuccess(
            this.dictionaryService.get('LeavesGrantedSuccess') || 'Leaves granted successfully',
          );

          this.grantLeavesForm.reset();

          this.usersSelect.checkOption('', 'none');
          this.typeSelect.updateSelectedOption('');

          this.usersIds = [];
        } else {
          this.snackbarService.showError(
            this.dictionaryService.get('LeavesGrantFailed') || res?.message || 'Failed to grant leaves',
          );
        }
      },
      error: (err) => {
        this.snackbarService.showError(this.dictionaryService.get('NetworkError') || 'Network error, please try again');
      },
      complete: () => {
        this.submitting.set(false);
      },
    });
  }
}
